import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Menu, X, Search } from 'lucide-react';
import { useCart } from '../../context/CartContext';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Collections', path: '/category/all' },
    { name: 'Living Room', path: '/category/living-room' },
    { name: 'Bedroom', path: '/category/bedroom' },
    { name: 'Dining', path: '/category/dining' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const baseTextColor = isHomePage && !isScrolled ? 'text-white' : 'text-[#2D4A3E]';
  const logoColor = isHomePage && !isScrolled ? 'text-white' : 'text-[#2D4A3E]';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? 'bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#E8E4DE] py-4'
            : 'bg-transparent py-6'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className={`font-['Cormorant_Garamond'] text-2xl md:text-3xl font-semibold tracking-tight ${logoColor}`}
              data-testid="logo"
            >
              Bright Vista
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-['Montserrat'] text-xs uppercase tracking-[0.12em] font-medium hover:text-[#C67D4E] transition-colors ${baseTextColor}`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop Icons */}
            <div className="hidden lg:flex items-center gap-5">
              <button 
                className={`hover:text-[#C67D4E] transition-colors ${baseTextColor}`}
                data-testid="search-btn"
              >
                <Search size={20} />
              </button>
              <Link 
                to="/cart" 
                className={`relative hover:text-[#C67D4E] transition-colors ${baseTextColor}`}
                data-testid="cart-btn"
              >
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#C67D4E] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-4">
              <Link 
                to="/cart" 
                className={`relative ${baseTextColor}`}
                data-testid="mobile-cart-btn"
              >
                <ShoppingBag size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#C67D4E] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={baseTextColor}
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#FAF8F5] lg:hidden"
          >
            <div className="pt-24 px-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] hover:text-[#C67D4E] transition-colors"
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
